import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from './AuthContext';

type OrgType = 'client' | 'agent' | 'supplier';

interface Organization {
  id: string;
  name: string;
  orgType: OrgType;
  logoUrl?: string;
  status: string;
  primaryContactEmail?: string;
}

interface OrganizationMembership {
  organizationId: string;
  organization: Organization;
  role: string;
  isPrimary: boolean;
  joinedAt: string;
}

interface OrganizationContextType {
  // Current organization
  currentOrganization: Organization | null;
  currentMembership: OrganizationMembership | null;
  
  // All organizations user belongs to
  organizations: OrganizationMembership[];
  
  // Loading state
  loading: boolean;
  
  // Actions
  switchOrganization: (organizationId: string) => Promise<void>;
  refreshOrganizations: () => Promise<void>;
  
  // Helpers
  hasMultipleOrganizations: boolean;
  isAgent: boolean;
  isClient: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const CURRENT_ORG_KEY = 'faces_current_org_id';

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  
  const [organizations, setOrganizations] = useState<OrganizationMembership[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [currentMembership, setCurrentMembership] = useState<OrganizationMembership | null>(null);
  const [loading, setLoading] = useState(true);

  // Legacy lookup for users - directly query existing tables
  // OPTIMIZED: Moved this function definition before loadOrganizations to fix reference
  const loadLegacyOrganization = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      // Get organization through profiles.client_id (direct link to clients table)
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('client_id')
        .eq('id', user.id)
        .single();

      if (profileError || !profile?.client_id) {
        // User has no client association - this is normal for admins
        // User has no client association - this is normal for admins
        setLoading(false);
        return;
      }

      // Fetch the client details
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .select('id, name, tenant_type, status, primary_contact_email')
        .eq('id', profile.client_id)
        .single();

      if (clientError || !client) {
        // Client not found for client_id
        setLoading(false);
        return;
      }

      const org: Organization = {
        id: client.id,
        name: client.name,
        orgType: (client.tenant_type === 'agent' ? 'agent' : 'client') as 'client' | 'agent',
        logoUrl: undefined, // clients table doesn't have logo_url
        status: client.status ?? undefined,
        primaryContactEmail: client.primary_contact_email ?? undefined
      };

      const membership: OrganizationMembership = {
        organizationId: client.id,
        organization: org,
        role: 'member',
        isPrimary: true,
        joinedAt: new Date().toISOString()
      };

      setOrganizations([membership]);
      setCurrentOrganization(org);
      setCurrentMembership(membership);
      localStorage.setItem(CURRENT_ORG_KEY, client.id);
    } catch (err) {
      console.error('[OrganizationContext] Legacy lookup error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load user's organizations
  // OPTIMIZED: Skip query to non-existent user_organization_memberships table
  // This table doesn't exist and causes 404 errors that slow down initialization
  const loadOrganizations = useCallback(async () => {
    if (!user?.id) {
      setOrganizations([]);
      setCurrentOrganization(null);
      setCurrentMembership(null);
      setLoading(false);
      return;
    }

    // Skip the user_organization_memberships query entirely as the table doesn't exist
    // This was causing repeated 404 errors and slowing down page load significantly
    // Go directly to legacy organization lookup which uses existing tables
    await loadLegacyOrganization();
  }, [user?.id, loadLegacyOrganization]);


  // Switch to a different organization
  const switchOrganization = useCallback(async (organizationId: string) => {
    const membership = organizations.find(m => m.organizationId === organizationId);
    
    if (!membership) {
      console.error('[OrganizationContext] Organization not found:', organizationId);
      return;
    }

    setCurrentOrganization(membership.organization);
    setCurrentMembership(membership);
    localStorage.setItem(CURRENT_ORG_KEY, organizationId);

    // Emit event for other components to react
    window.dispatchEvent(new CustomEvent('organization-switched', { 
      detail: { organizationId, organization: membership.organization } 
    }));
  }, [organizations]);

  // Refresh organizations
  const refreshOrganizations = useCallback(async () => {
    setLoading(true);
    await loadOrganizations();
  }, [loadOrganizations]);

  // Load on auth change
  useEffect(() => {
    if (isAuthenticated) {
      loadOrganizations();
    } else {
      setOrganizations([]);
      setCurrentOrganization(null);
      setCurrentMembership(null);
      setLoading(false);
      localStorage.removeItem(CURRENT_ORG_KEY);
    }
  }, [isAuthenticated, loadOrganizations]);

  const value: OrganizationContextType = {
    currentOrganization,
    currentMembership,
    organizations,
    loading,
    switchOrganization,
    refreshOrganizations,
    hasMultipleOrganizations: organizations.length > 1,
    isAgent: currentOrganization?.orgType === 'agent',
    isClient: currentOrganization?.orgType === 'client'
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  
  return context;
};

// Hook for listening to organization switches
export const useOrganizationSwitch = (callback: (organizationId: string) => void) => {
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      callback(event.detail.organizationId);
    };

    window.addEventListener('organization-switched', handler as EventListener);
    return () => {
      window.removeEventListener('organization-switched', handler as EventListener);
    };
  }, [callback]);
};

export default OrganizationContext;
